import { Component, OnInit, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/shared/material.module';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';

import { BusinessError, CardsService, CreateCardForm, DigitalCard } from '../../services/cards.service';
import { PaymentsComponent } from '../payments/payments.component';
import { GalleryComponent } from '../gallery/gallery.component';
import { SettingsComponent } from '../settings/settings.component';
import { AboutComponent } from '../about/about.component';
import { MembersComponent } from '../members/members.component';
import { ListingService } from 'src/app/services/listing.service';
import { ListingComponent } from '../listing/listing.component';

type ListingType = 'product' | 'service' | 'events' | 'charity' | 'reviews' | 'projects';

@Component({
  selector: 'app-view-card',
  standalone: true,
  templateUrl: './view-card.component.html',
  styleUrls: ['./view-card.component.scss'],
  imports: [
    RouterModule,
    CommonModule,
    MaterialModule,
    FormsModule,
    ListingComponent,    // <- the shared listing UI
    PaymentsComponent, 
    GalleryComponent,
    SettingsComponent,
    AboutComponent,
    MembersComponent
  ]
})
export class ViewCardComponent implements OnInit, OnDestroy {
  card: DigitalCard | null = null;
  cardId = '';
  flipped = false;
  mobileMenuOpen = false;
loadedListings: any = null; // ✅ Store loaded listings data
  // Edit Modal State
  showEditForm = false;
  editingCard: DigitalCard | null = null;
  updating = false;
  updateError: BusinessError | null = null;

  activePanel: string | null = null;

  editCardForm: CreateCardForm = {
    name: '',
    phone: '',
    email: '',
    handler: '',
    language: 'en',
    type: 'Business',
    industry: '',
    photo: '',
    businessName: '',
  };

  // Options for dropdowns
  languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'Hindi' },
    { value: 'ta', label: 'Tamil' },
    { value: 'te', label: 'Telugu' },
    { value: 'kn', label: 'Kannada' },
    { value: 'ml', label: 'Malayalam' }
  ];
  cardTypeOptions = [
    { value: 'Business', label: 'Business Card' },
    { value: 'Personal', label: 'Personal Card' }
  ];
  industryOptions = [
    { value: '', label: 'Select Industry' },
    { value: 'Technology', label: 'Technology & IT' },
    { value: 'Healthcare', label: 'Healthcare & Medical' },
    { value: 'Finance', label: 'Finance & Banking' },
    { value: 'Education', label: 'Education & Training' },
    { value: 'Retail', label: 'Retail & E-commerce' },
    { value: 'Manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'Professional Services', label: 'Professional Services' },
    { value: 'Real Estate', label: 'Real Estate' },
    { value: 'Food & Beverage', label: 'Food & Beverage' },
    { value: 'Entertainment', label: 'Entertainment & Media' },
    { value: 'Transportation', label: 'Transportation & Logistics' },
    { value: 'Construction', label: 'Construction & Engineering' },
    { value: 'Agriculture', label: 'Agriculture & Farming' },
    { value: 'Legal', label: 'Legal Services' },
    { value: 'Marketing', label: 'Marketing & Advertising' },
    { value: 'Consulting', label: 'Consulting' },
    { value: 'Non-Profit', label: 'Non-Profit & NGO' },
    { value: 'Government', label: 'Government & Public Sector' },
    { value: 'Other', label: 'Other' }
  ];

  // Panels that the ListingComponent will handle
  private listingPanels = ['products', 'services', 'events', 'reviews', 'charity', 'projects'];

  @ViewChild(ListingComponent) listingCmp?: ListingComponent;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cardsService: CardsService,
    private renderer: Renderer2,
    private listingService: ListingService
  ) { }

  ngOnInit(): void {
    this.cardId = this.route.snapshot.paramMap.get('id') || '';
    this.loadCard();
    
    // Check for panel query parameter (for returning from AI chat)
    const panelParam = this.route.snapshot.queryParamMap.get('panel');
    const shouldOpenForm = this.route.snapshot.queryParamMap.get('openForm') === 'true';
    
    if (panelParam && ['products', 'services', 'events', 'reviews', 'charity', 'about', 'settings', 'payments', 'gallery'].includes(panelParam)) {
      // Set the specified panel as active
      this.activePanel = panelParam;
      this.renderer.addClass(document.body, 'panel-open');
      
      console.log('🔄 Restored panel from query param:', panelParam);
      
      // If we should auto-open the create form, trigger it after a delay
      if (shouldOpenForm && ['products', 'services', 'events', 'reviews', 'charity'].includes(panelParam)) {
        setTimeout(() => {
          console.log('🔄 Auto-opening create form for panel:', panelParam);
          // The listing component should handle the auto-opening via its own query parameter handling
        }, 500);
      }
    } else {
      // Default to About panel
      this.activePanel = 'about';
      this.renderer.addClass(document.body, 'panel-open');
    }

    this.router.events.subscribe(() => {
      this.mobileMenuOpen = false;
    });
  }

  ngOnDestroy(): void {
    this.closePanel();
    this.renderer.removeClass(document.body, 'panel-open');
    this.renderer.removeClass(document.body, 'offcanvas-open');
  }

  async loadCard(): Promise<void> {
  if (!this.cardId) { this.goBack(); return; }
  try {
    this.card = await this.cardsService.getCardById(this.cardId);
    console.log('Loaded card data:', this.card); 
    if (this.card) {
      // ✅ Load data once and store it
      this.listingService.getListingsOnce(this.card.id).subscribe({
        next: (data) => {
          this.loadedListings = data;
          console.log('[ViewCard] Loaded listings data:', data);
        },
        error: (err) => console.error('[ViewCard] Failed to load listings:', err)
      });
    }
  } catch (err) {
    console.error('Failed to load card:', err);
    this.goBack();
  }
}

  /** Opens edit modal and populates with existing card data */
  openEditForm(card: DigitalCard | null): void {
    if (!card) return;
    this.showEditForm = true;
    this.editingCard = card;
    this.populateEditForm(card);
  }

  private populateEditForm(card: DigitalCard): void {
    this.editCardForm = {
      name: card.contact_info?.contactName || '',
      phone: card.contact_info?.primaryPhone || '',
      email: card.contact_info?.primaryEmail || '',
      handler: card.handler || '',
      language: card.language || 'en',
      type: card.type || 'Business',
      industry: card.industry || '',
      website: '',
      address: card.personalInfo?.address || '',
      dob: card.personalInfo?.dob || '',
      gender: card.personalInfo?.gender || '',
      photo: card.personalInfo?.profilePicture || '',
      businessName: card.business_info?.businessName || '',

    };
    this.updateError = null;
  }

  closeEditForm(): void {
    this.showEditForm = false;
    this.editingCard = null;
    this.clearEditForm();
  }

  private clearEditForm(): void {
    this.editCardForm = {
      name: '',
      phone: '',
      email: '',
      handler: '',
      language: 'en',
      type: 'Business',
      industry: '',
      website: '',
      address: '',
      dob: '',
      gender: '',
      photo: '',
      businessName: '',
      jobTitle: '',
      department: '',
      location: ''
    };
    this.updateError = null;
  }

  async saveCardEdits(): Promise<void> {
    if (!this.editingCard?.id) {
      this.updateError = {
        type: 'business',
        title: 'No Card Selected',
        message: 'Card is not loaded for editing.',
        code: 'NO_CARD_SELECTED'
      };
      return;
    }
    try {
      this.updating = true;
      this.updateError = null;

      if (!this.editCardForm.name?.trim()) {
        this.updateError = { type: 'validation', title: 'Validation Error', message: 'Contact name is required', code: 'MISSING_NAME' };
        return;
      }
      if (!this.editCardForm.phone?.trim()) {
        this.updateError = { type: 'validation', title: 'Validation Error', message: 'Phone number is required', code: 'MISSING_PHONE' };
        return;
      }

      await this.cardsService.updateCardPersonalInfo(this.editingCard.id, {
        address: this.editCardForm.address || '',
        dob: this.editCardForm.dob || '',
        gender: this.editCardForm.gender || '',
        mobile: this.editCardForm.phone.trim(),
        profilePicture: this.editCardForm.photo || ''
      });

      await this.cardsService.updateCardContact(this.editingCard.id, {
        primaryPhone: this.editCardForm.phone.trim(),
        primaryEmail: this.editCardForm.email?.trim() || '',
        contactName: this.editCardForm.name.trim()
      });

      await this.cardsService.updateCardBusinessInfo(this.editingCard.id, {
        businessName: this.editCardForm.businessName || '',

      });

      const refreshed = await this.cardsService.getCardById(this.editingCard.id, true);
      this.card = refreshed;
      this.cardsService.updateCardInState(refreshed);
      this.cardsService.setSelectedCard(refreshed);

      this.closeEditForm();
    } catch (err: unknown) {
      console.error('Error updating card:', err);
      this.updateError = { type: 'business', title: 'Update Failed', code: 'CARD_UPDATE_ERROR', message: 'Failed to update card details. ' + (err instanceof Error ? err.message : 'Unknown error') };
    } finally {
      this.updating = false;
    }
  }

  // Panel toggle methods
  toggleProductsPanel(): void { this.setActivePanel('products'); }
  toggleMembersPanel(): void { 
    // Only allow members panel for business cards
    if (this.card?.type === 'Business') {
      this.setActivePanel('members'); 
    }
  }
  toggleServicesPanel(): void { this.setActivePanel('services'); }
  toggleEventsPanel(): void { this.setActivePanel('events'); }
  toggleReviewsPanel(): void { this.setActivePanel('reviews'); }
  toggleCharityPanel(): void { this.setActivePanel('charity'); }
  toggleSettingsPanel(): void { this.setActivePanel('settings'); }
  toggleAboutPanel(): void { this.setActivePanel('about'); }
  togglePaymentsPanel(): void { this.setActivePanel('payments'); }
  toggleGalleryPanel(): void { this.setActivePanel('gallery'); }
  toggleProjectsPanel() {
    this.activePanel = 'projects';
  }

  private setActivePanel(panelName: string): void {
    if (this.activePanel === panelName) {
      this.closePanel();
    } else {
      this.activePanel = panelName;
      this.renderer.addClass(document.body, 'panel-open');
    }
  }
  closePanelAndGoBack() {
  // Reset active panel
  this.activePanel = null;

  // Navigate to Cards page
  this.router.navigate(['/cards']);
}


  closePanel(): void {
    this.activePanel = null;
    this.renderer.removeClass(document.body, 'panel-open');
  }

  getPanelTitle(): string {
    const titles: { [key: string]: string } = {
      'products': 'Products',
      'services': 'Services',
      'events': 'Events',
      'reviews': 'Reviews',
      'charity': 'Charity',
      'settings': 'Settings',
      'payments': 'Payments',
      'about': 'About',
      'members': 'Members'
    };
    return titles[this.activePanel || ''] || '';
  }

  getPanelIcon(): string {
    const icons: { [key: string]: string } = {
      'products': 'bi-box-seam',
      'services': 'bi-gear',
      'events': 'bi-calendar-event',
      'reviews': 'bi-star',
      'charity': 'bi-heart',
      'settings': 'bi-sliders',
      'payments': 'bi-credit-card',
      'about': 'bi-info-circle',
      'members': 'bi-people'
    };
    return icons[this.activePanel || ''] || '';
  }

  // Helpers to decide and map listing panels
  isListingPanel(panel: string): boolean {
    return ['products', 'services', 'events', 'reviews', 'charity', 'projects'].includes(panel);
  }

  mapPanelToListingType(panel: string): ListingType | undefined {
    switch (panel) {
      case 'products': return 'product';
      case 'services': return 'service';
      case 'events': return 'events';
      case 'reviews': return 'reviews';
      case 'charity': return 'charity';
      case 'projects': return 'projects';
      default: return undefined;
    }
  }

  // Call listing child helper (optional)
  openListingCreateAndGenerate(): void {
    this.listingCmp?.openCreateFormAndGenerateContent?.();
  }

  // Utilities
  flipCard(): void { this.flipped = !this.flipped; }
  goBack(): void { this.router.navigate(['/cards']); }
  // formatDate(dateString?: string): string {
  //   if (!dateString) return 'N/A';
  //   try {
  //     return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  //   } catch { return 'Invalid Date'; }
  // }
  getInitials(name: string): string {
    return (name || '').split(' ').map(part => part[0] || '').join('').toUpperCase().slice(0, 2);
  }
  scrollTo(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    if (this.mobileMenuOpen) this.renderer.addClass(document.body, 'offcanvas-open');
    else this.renderer.removeClass(document.body, 'offcanvas-open');
  }
  clickAndClose(sectionId: string): void {
    this.scrollTo(sectionId);
    this.mobileMenuOpen = false;
  }
}